import { useState, useEffect } from 'react';
import { useNavigation } from '@refinedev/core';
import {
  Form,
  Input,
  InputNumber,
  Card,
  Row,
  Col,
  Button,
  Select,
  message,
  Collapse,
  Divider,
  Space,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  LockOutlined,
  BankOutlined,
  UserOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';

interface BusCompany {
  id: number;
  name: string;
  username: string;
  contactPhone: string;
}

export const BusStaffCreate = () => {
  const [form] = Form.useForm();
  const { list } = useNavigation();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [companies, setCompanies] = useState<BusCompany[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Fetch existing companies for "update credentials" mode
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await apiClient.get('/admin/bus/companies', {
          params: { limit: 500 },
        });
        setCompanies(response.data.data || []);
      } catch {
        // Ignore
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      if (mode === 'existing') {
        // Update existing company credentials
        const companyId = values.companyId;
        const updateData: Record<string, unknown> = {};
        if (values.username) updateData.username = values.username;
        if (values.password) updateData.password = values.password;

        await apiClient.put(`/admin/bus/companies/${companyId}`, updateData);
        message.success('Kompaniya ma\'lumotlari yangilandi');
      } else {
        // Create new company
        await apiClient.post('/admin/bus/companies', values);
        message.success('Yangi kompaniya profili yaratildi');
      }
      list('bus/staff');
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Xatolik yuz berdi';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = (companyId: number) => {
    const company = companies.find((c) => c.id === companyId);
    if (company) {
      form.setFieldsValue({
        username: company.username,
      });
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => list('bus/staff')}>
          Orqaga
        </Button>
        <h2 style={{ margin: 0 }}>Yangi xodim (Kompaniya profili)</h2>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button
            type={mode === 'new' ? 'primary' : 'default'}
            icon={<BankOutlined />}
            onClick={() => {
              setMode('new');
              form.resetFields();
            }}
          >
            Yangi kompaniya
          </Button>
          <Button
            type={mode === 'existing' ? 'primary' : 'default'}
            icon={<LockOutlined />}
            onClick={() => {
              setMode('existing');
              form.resetFields();
            }}
          >
            Mavjud kompaniya uchun parol
          </Button>
        </Space>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {mode === 'existing' ? (
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Card title="Kompaniya tanlash" style={{ marginBottom: 24 }}>
                <Form.Item
                  label="Kompaniya"
                  name="companyId"
                  rules={[{ required: true, message: 'Kompaniyani tanlang' }]}
                >
                  <Select
                    placeholder="Kompaniyani tanlang"
                    showSearch
                    loading={loadingCompanies}
                    onChange={handleCompanySelect}
                    options={companies.map((c) => ({
                      value: c.id,
                      label: `${c.name} (${c.username})`,
                    }))}
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Card>

              <Card
                title={
                  <span>
                    <LockOutlined /> Yangi kirish ma'lumotlari
                  </span>
                }
              >
                <Form.Item
                  label="Login (username)"
                  name="username"
                  rules={[{ min: 3, message: "Kamida 3 ta belgi" }]}
                >
                  <Input placeholder="safargoexpress" prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item
                  label="Yangi parol"
                  name="password"
                  rules={[
                    { required: true, message: 'Parolni kiriting' },
                    { min: 6, message: "Kamida 6 ta belgi" },
                  ]}
                >
                  <Input.Password placeholder="Yangi parol" prefix={<LockOutlined />} />
                </Form.Item>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    <BankOutlined /> Kompaniya ma'lumotlari
                  </span>
                }
              >
                <Form.Item
                  label="Kompaniya nomi"
                  name="name"
                  rules={[{ required: true, message: 'Nomni kiriting' }]}
                >
                  <Input placeholder="SafarGo Express" />
                </Form.Item>

                <Form.Item label="Yuridik nomi" name="legalName">
                  <Input placeholder="SafarGo Express MCHJ" />
                </Form.Item>

                <Form.Item
                  label="Telefon raqam"
                  name="contactPhone"
                  rules={[
                    { required: true, message: 'Telefon raqamni kiriting' },
                    { pattern: /^\+998\d{9}$/, message: 'Format: +998XXXXXXXXX' },
                  ]}
                >
                  <Input placeholder="+998901234567" />
                </Form.Item>

                <Form.Item label="Email" name="contactEmail">
                  <Input placeholder="info@company.uz" type="email" />
                </Form.Item>

                <Form.Item
                  label="Komissiya foizi"
                  name="commissionPercent"
                  initialValue={10}
                >
                  <InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" />
                </Form.Item>
              </Card>

              <Card
                title={
                  <span>
                    <LockOutlined /> Tizimga kirish
                  </span>
                }
                style={{ marginTop: 24 }}
              >
                <Form.Item
                  label="Login (username)"
                  name="username"
                  rules={[
                    { required: true, message: 'Loginni kiriting' },
                    { min: 3, message: "Kamida 3 ta belgi" },
                  ]}
                >
                  <Input placeholder="safargoexpress" prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item
                  label="Parol"
                  name="password"
                  rules={[
                    { required: true, message: 'Parolni kiriting' },
                    { min: 6, message: "Kamida 6 ta belgi" },
                  ]}
                >
                  <Input.Password placeholder="Kamida 6 ta belgi" prefix={<LockOutlined />} />
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Collapse
                ghost
                items={[
                  {
                    key: 'legal',
                    label: (
                      <span style={{ fontWeight: 500 }}>
                        <FileTextOutlined /> Yuridik ma'lumotlar (ixtiyoriy)
                      </span>
                    ),
                    children: (
                      <>
                        <Form.Item label="Tashkiliy shakli" name="businessForm">
                          <Select
                            placeholder="Tanlang"
                            allowClear
                            options={[
                              { value: 'MCHJ', label: 'MCHJ' },
                              { value: 'YaTT', label: 'YaTT (Yakka tartibdagi tadbirkor)' },
                              { value: 'AJ', label: 'AJ (Aksiyadorlik jamiyati)' },
                              { value: 'QK', label: "QK (Qo'shma korxona)" },
                              { value: 'UK', label: 'UK (Unitar korxona)' },
                            ]}
                          />
                        </Form.Item>

                        <Form.Item label="STIR (INN)" name="inn">
                          <Input placeholder="123456789" />
                        </Form.Item>

                        <Form.Item label="Yuridik manzil" name="legalAddress">
                          <Input.TextArea placeholder="Yuridik manzil" rows={2} />
                        </Form.Item>

                        <Form.Item label="Haqiqiy manzil" name="actualAddress">
                          <Input.TextArea placeholder="Haqiqiy manzil" rows={2} />
                        </Form.Item>

                        <Divider>Bank ma'lumotlari</Divider>

                        <Form.Item label="Bank hisobi" name="bankAccount">
                          <Input placeholder="20208000123456789012" />
                        </Form.Item>

                        <Form.Item label="Bank nomi" name="bankName">
                          <Input placeholder="Ipoteka Bank" />
                        </Form.Item>

                        <Divider>Direktor</Divider>

                        <Form.Item label="Direktor F.I.O" name="directorFullName">
                          <Input placeholder="Alijon Valiyev" />
                        </Form.Item>

                        <Form.Item label="Pasport raqami" name="directorPassportNumber">
                          <Input placeholder="AA1234567" />
                        </Form.Item>

                        <Divider>Shartnoma</Divider>

                        <Form.Item label="Shartnoma raqami" name="contractNumber">
                          <Input placeholder="SG-2024-001" />
                        </Form.Item>
                      </>
                    ),
                  },
                ]}
              />
            </Col>
          </Row>
        )}

        <div style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SaveOutlined />}
            size="large"
          >
            {mode === 'existing' ? 'Parolni yangilash' : 'Profil yaratish'}
          </Button>
        </div>
      </Form>
    </div>
  );
};
